import { useState, useEffect, Dispatch, SetStateAction } from 'react'

/**
 * A hook that persists state to localStorage and syncs it across browser tabs.
 * 
 * @template T The type of the value to store in localStorage
 * @param key - The key to use in localStorage
 * @param initialValue - The initial value to use if no value is found in localStorage
 * @returns A tuple containing the stored value and a function to update it, similar to useState
 * 
 * @example
 * // Basic usage with a string
 * const [name, setName] = useLocalStorage('user-name', 'John')
 * 
 * @example
 * // Usage with an object
 * interface UserPreferences {
 *   theme: 'light' | 'dark'
 *   fontSize: number
 * }
 * const [preferences, setPreferences] = useLocalStorage<UserPreferences>('user-preferences', {
 *   theme: 'light',
 *   fontSize: 16
 * })
 * 
 * @example
 * // Usage with a function updater (like useState)
 * const [count, setCount] = useLocalStorage('counter', 0)
 * setCount(prev => prev + 1)
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  // Get from local storage then parse stored json or return initialValue
  const readValue = (): T => {
    // Prevent build error "window is undefined" but keep working
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      if (item === null) {
        return initialValue
      }

      try {
        return JSON.parse(item) as T
      } catch (parseError) {
        console.warn(
          `Error parsing localStorage key "${key}":`,
          parseError,
          'Falling back to initial value'
        )
        return initialValue
      }
    } catch (error) {
      console.warn(
        `Error reading localStorage key "${key}":`,
        error,
        'Falling back to initial value'
      )
      return initialValue
    }
  }

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(readValue)

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value

      // Save to state
      setStoredValue(valueToStore)

      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
      }
    } catch (error) {
      console.warn(
        `Error setting localStorage key "${key}":`,
        error,
        'State was updated but not persisted to localStorage'
      )
    }
  }

  // Listen for changes to this localStorage key in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) {
        try {
          setStoredValue(JSON.parse(event.newValue) as T)
        } catch (error) {
          console.warn(
            `Error parsing localStorage value from storage event for key "${key}":`,
            error
          )
        }
      }
    }

    // Add event listener
    window.addEventListener('storage', handleStorageChange)

    // Remove event listener on cleanup
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  // Update stored value if key changes
  useEffect(() => {
    setStoredValue(readValue())
  }, [key])

  return [storedValue, setValue]
}

export const FRONT_UUID_STORAGE_KEY = 'front-uuid'
export const IP_ADDRESS_STORAGE_KEY = 'user-ip-address'
export const PRICING_RECRUITMENT_METHOD_STORAGE_KEY = 'pricing-recruitment-method'
export const PRICING_FIRM_SIZE_STORAGE_KEY = 'pricing-firm-size'
export const PRICING_FREQUENCY_SELECTOR_STORAGE_KEY = 'pricing-frequency-selector'
export const IS_BASE_BANNER_LIKES_OPEN_STORAGE_KEY = 'is-base-banner-open-likes'
export const FINGERPRINT_STORAGE_KEY = 'fingerprint-js-visitor-id'
